import { useState } from "react";
import { RotateCcw, Trash2, Eye, FileText, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ProcessedFile } from "@/components/InvoiceList";
import type { FailedFile } from "@/components/FailedFileSplitView";
import type { TrashedFile } from "@/hooks/useInvoiceSession";
import FileViewer from "@/components/FileViewer";

interface InvoiceTrashProps {
  trashedFiles: TrashedFile[];
  onRestoreProcessed: (file: ProcessedFile) => void;
  onRestoreFailed: (file: FailedFile) => void;
  onPermanentDelete: (id: string) => void;
  onEmptyTrash: () => void;
}

export default function InvoiceTrash({
  trashedFiles,
  onRestoreProcessed,
  onRestoreFailed,
  onPermanentDelete,
  onEmptyTrash,
}: InvoiceTrashProps) {
  const [open, setOpen] = useState(true);
  const [previewFile, setPreviewFile] = useState<File | null>(null);

  if (trashedFiles.length === 0) return null;

  return (
    <div className="rounded-lg border border-dashed border-muted-foreground/30 bg-muted/30">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <Trash2 className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-muted-foreground">
            Papelera ({trashedFiles.length})
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-destructive hover:bg-destructive/10"
            onClick={(e) => {
              e.stopPropagation();
              onEmptyTrash();
            }}
          >
            Vaciar papelera
          </Button>
          {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </div>
      </button>

      {open && (
        <div className="space-y-2 px-4 pb-4">
          {trashedFiles.map((item) => {
            const isProcessed = item.origin === "processed";
            const file = item.file;
            const label = isProcessed
              ? (file as ProcessedFile).newName || (file as ProcessedFile).originalName
              : file.file.name;
            return (
              <div
                key={file.id}
                className="flex items-center gap-3 rounded-md border bg-background px-3 py-2"
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-foreground">{label}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {isProcessed ? "Factura procesada" : "No procesada"}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-primary"
                    title="Volver a visualizar"
                    onClick={() => setPreviewFile(file.file)}

                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-primary"
                    title="Recuperar"
                    onClick={() => {
                      if (isProcessed) onRestoreProcessed(file as ProcessedFile);
                      else onRestoreFailed(file as FailedFile);
                    }}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    title="Eliminar definitivamente"
                    onClick={() => onPermanentDelete(file.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <FileViewer file={previewFile} open={previewFile !== null} onClose={() => setPreviewFile(null)} />
    </div>
  );
}
